/**
 * Table Component
 *
 * A responsive HTML table with consistent styling.
 * Use for displaying tabular data with rows and columns.
 *
 * @example
 * ```tsx
 * <Table>
 *   <TableHeader>
 *     <TableRow>
 *       <TableHead>Name</TableHead>
 *       <TableHead>Email</TableHead>
 *     </TableRow>
 *   </TableHeader>
 *   <TableBody>
 *     <TableRow>
 *       <TableCell>John Doe</TableCell>
 *       <TableCell>john@example.com</TableCell>
 *     </TableRow>
 *   </TableBody>
 * </Table>
 * ```
 */

'use client';

import { cn } from '../utils';
import * as React from 'react';

// =============================================================================
// TABLE
// =============================================================================

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div data-slot="table-container" className="relative w-full overflow-x-auto">
    <table
      ref={ref}
      data-slot="table"
      className={cn('w-full caption-bottom text-sm', className)}
      {...props}
    />
  </div>
));
Table.displayName = 'Table';

// =============================================================================
// TABLE HEADER
// =============================================================================

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    data-slot="table-header"
    className={cn(
      'bg-(--table-header-bg)',
      '[&_tr]:border-b [&_tr]:border-[var(--table-header-border-color,var(--table-border-color,var(--border)))]',
      className,
    )}
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

// =============================================================================
// TABLE BODY
// =============================================================================

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    data-slot="table-body"
    className={cn('[&_tr:last-child]:border-0', className)}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

// =============================================================================
// TABLE FOOTER
// =============================================================================

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    data-slot="table-footer"
    className={cn(
      'bg-muted/50 border-t font-medium [&>tr]:last:border-b-0',
      className,
    )}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

// =============================================================================
// TABLE ROW
// =============================================================================

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    data-slot="table-row"
    className={cn(
      'border-b border-(--table-border-color) transition-colors',
      'hover:bg-(--table-row-hover-bg) data-[state=selected]:bg-muted',
      className,
    )}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

// =============================================================================
// TABLE HEAD
// =============================================================================

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    data-slot="table-head"
    className={cn(
      'py-3 px-4 text-left align-middle',
      // Use explicit type hints for Tailwind arbitrary values
      // text-[length:...] for font-size, text-[color:...] for color
      'text-[length:var(--table-header-font-size,14px)] font-[var(--table-header-font-weight,600)]',
      'text-[color:var(--table-header-fg,var(--foreground))] whitespace-nowrap',
      '[&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-0.5',
      className,
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

// =============================================================================
// TABLE CELL
// =============================================================================

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    data-slot="table-cell"
    className={cn(
      'py-[var(--table-cell-padding-y,0.75rem)] px-[var(--table-cell-padding-x,1rem)] align-middle',
      '[&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-0.5',
      className,
    )}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

// =============================================================================
// TABLE CAPTION
// =============================================================================

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    data-slot="table-caption"
    className={cn('mt-4 text-sm text-muted-foreground', className)}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

// =============================================================================
// EXPORTS
// =============================================================================

export {
    Table, TableBody, TableCaption, TableCell, TableFooter,
    TableHead, TableHeader, TableRow,
};

